// Pure helpers that turn editor HTML into a branded, email-client-safe message.
// Table layout + inline styles only (Gmail strips <style> blocks).
import {
  buildBanner,
  buildSignature,
  buildFooter,
  wrapEmail,
} from "./openHouseEmailsHtml";

// TES palette (tailwind.config.js): themeColor navy + linkColor blue, so the
// emails look like the app that sent them.
const BRAND_LINK = "#218FDA";
const SLATE = "#334155";

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// Plain-text sequence defaults → simple HTML (used until Stage 2 stores HTML).
export function plainTextToHtml(text) {
  const lines = String(text ?? "").split("\n");
  const out = [];
  let list = null; // accumulating <li>s
  let para = []; // accumulating paragraph lines

  const flushPara = () => {
    if (para.length) {
      out.push(`<p>${para.map(escapeHtml).join("<br>")}</p>`);
      para = [];
    }
  };
  const flushList = () => {
    if (list) {
      out.push(`<ul>${list.map((li) => `<li>${escapeHtml(li)}</li>`).join("")}</ul>`);
      list = null;
    }
  };

  for (const raw of lines) {
    const line = raw.trimEnd();
    const bullet = line.match(/^\s*[•\-–]\s+(.*)$/);
    if (bullet) {
      flushPara();
      (list = list || []).push(bullet[1]);
    } else if (line.trim() === "") {
      flushPara();
      flushList();
    } else {
      flushList();
      para.push(line);
    }
  }
  flushPara();
  flushList();
  return out.join("");
}

// HTML body → readable plain text (the fallback `body` field on the wire).
export function htmlToPlainText(html) {
  let s = String(html ?? "");
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<li[^>]*>/gi, "• ").replace(/<\/li>/gi, "\n");
  s = s.replace(/<\/(p|div|ul|ol|h[1-6])>/gi, "\n\n");
  s = s.replace(/<[^>]+>/g, "");
  const el = typeof document !== "undefined" ? document.createElement("textarea") : null;
  if (el) {
    el.innerHTML = s;
    s = el.value;
  }
  return s.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
}

export function youtubeThumbnail(url) {
  const m = String(url || "").match(
    /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/
  );
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
}

// Click-to-play card. Email clients can't play video inline; this is the
// industry-standard thumbnail-that-links-out.
export function videoCardHtml({ url, title, thumbnailUrl }) {
  if (!url) return "";
  const safeUrl = escapeHtml(url);
  const safeTitle = escapeHtml(title || "Watch video");
  const thumb = thumbnailUrl || youtubeThumbnail(url);
  const inner = thumb
    ? `<img src="${escapeHtml(thumb)}" alt="${safeTitle}" width="480" style="display:block;width:100%;max-width:480px;border-radius:10px;" />`
    : `<div style="background:#0f172a;border-radius:10px;padding:48px 16px;text-align:center;color:#ffffff;font-size:16px;">&#9654;&nbsp;&nbsp;${safeTitle}</div>`;
  return `
  <table role="presentation" cellpadding="0" cellspacing="0" style="margin:16px 0;width:100%;">
    <tr><td>
      <a href="${safeUrl}" target="_blank" style="text-decoration:none;color:${SLATE};">
        ${inner}
        <div style="font-size:13px;color:${BRAND_LINK};padding-top:6px;">&#9654; ${safeTitle}</div>
      </a>
    </td></tr>
  </table>`;
}

// ONE email design for the whole app: transaction emails are wrapped with the
// SAME banner / signature / footer builders the Open House emails and the
// Settings → Email Templates preview use (openHouseEmailsHtml.js) — navy
// banner with the hosted white logo + property address, agent signature with
// photo, and footer. Never a second look.
export function wrapBrandedEmail({ contentHtml, sender, videoCard, propertyAddress }) {
  return wrapEmail({
    banner: buildBanner({ propertyAddress: propertyAddress || "" }, ""),
    body: `${contentHtml || ""}${videoCard || ""}`,
    signature: buildSignature(sender || {}),
    footer: buildFooter(
      "You're receiving this because we're working on your transaction with Team Edelstein.",
    ),
  });
}
